import { useInfiniteQuery } from '@tanstack/react-query'

import { useAuth } from '../AuthContext'
import { useSelectedHospital } from '../SelectedHospitalContext'
import { BedDiff } from '../components/BedDiff'
import { DataTable, type Column } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { AUDIT_LOG_KEY, fetchAuditLog, type AuditEntry } from '../api/auditLog'

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diffMs / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

function ChangeCell({ entry }: { entry: AuditEntry }) {
  if (entry.action === 'bed_inventory.update') {
    return <BedDiff before={entry.before} after={entry.after} />
  }
  return (
    <details>
      <summary className="cursor-pointer text-ink-3">View</summary>
      <pre className="mt-1 whitespace-pre-wrap text-xs text-ink-2">
        {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
      </pre>
    </details>
  )
}

export function AuditLogPage() {
  const { token } = useAuth()
  // amtc_admin's "Managing" picker scopes the log to one hospital; null = all.
  // A hospital_admin has no picker and is scoped server-side regardless.
  const { hospitalId } = useSelectedHospital()
  const {
    data,
    isPending,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...AUDIT_LOG_KEY, hospitalId],
    queryFn: ({ pageParam }) => fetchAuditLog(token, pageParam, hospitalId),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.page.next_cursor,
    enabled: Boolean(token),
  })

  const rows = data?.pages.flatMap((p) => p.data) ?? []

  const columns: Column<AuditEntry>[] = [
    {
      key: 'when',
      header: 'When',
      // A TIME. The frame fills this column with reference codes — AS-VHKDL,
      // AS-VHKDK — placeholder text left in the wrong column (M23 defect D5).
      // An audit log whose first column is not a time is not an audit log.
      cell: (e) => (
        <time
          dateTime={e.occurred_at}
          title={new Date(e.occurred_at).toLocaleString()}
          className="whitespace-nowrap"
        >
          {relativeTime(e.occurred_at)}
        </time>
      ),
    },
    {
      key: 'who',
      header: 'Who',
      cell: (e) => (
        <>
          <span className="block capitalize text-ink-1">
            {e.actor.role.replace(/_/g, ' ')}
          </span>
          <span className="block font-mono text-xs text-ink-3">
            {e.actor.user_id.slice(0, 8)}
          </span>
        </>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      // Mono chip, matching the Dashboard's activity feed — the two surfaces
      // show the same events and must render them identically.
      cell: (e) => (
        <span className="inline-block rounded bg-bg-sunken px-1.5 py-0.5 font-mono text-xs text-ink-2">
          {e.action}
        </span>
      ),
    },
    {
      key: 'detail',
      header: 'Detail',
      // Built from before/after. The frame's details are invented labels —
      // icu_update, generalbed_upd_v2, maternitybed_update_special — none of
      // which say what changed.
      cell: (e) => <ChangeCell entry={e} />,
    },
  ]

  return (
    // Four short columns should not be stretched to 1440; the frame's narrower
    // table is deliberate.
    <section className="flex max-w-4xl flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Audit Log
        </h1>
        <p className="mt-1 text-sm text-ink-3">
          Every action taken in the admin, in order.
        </p>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        getKey={(e) => String(e.id)}
        searchText={(e) => `${e.action} ${e.actor.role} ${e.target_table}`}
        caption="Recent changes to your hospital"
        isPending={isPending}
        isError={isError}
        errorMessage="Could not load the audit log."
        onRetry={() => void refetch()}
        paginated={hasNextPage === true}
        empty={
          <EmptyState title="No mutations yet.">
            Make an edit on the Beds page and it will appear here.
          </EmptyState>
        }
        {...(hasNextPage === true && {
          footer: (
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="inline-flex h-9 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft disabled:opacity-60"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          ),
        })}
      />
    </section>
  )
}
